import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { Button, Card, Notice, ProblemNotice, TextArea } from '../../components/ui'
import { listAppSettings, updateAppSetting, type AdminAppSetting } from '../../lib/api'

/** Runtime switches and the explicit, default-deny experiment cohort. */
export function Settings() {
  const [items, setItems] = useState<AdminAppSetting[] | null>(null)
  const [ids, setIds] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const [inputError, setInputError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(false)
  useEffect(() => {
    let cancelled = false
    void listAppSettings().then((result) => {
      if (cancelled) return
      const rows = result.items ?? []
      setItems(rows)
      const cohort = rows.find((row) => row.key === 'experiments.enabled_users')?.value
      setIds(Array.isArray(cohort) ? cohort.filter((id): id is string => typeof id === 'string').join('\n') : '')
      setError(null)
    }).catch((cause: unknown) => { if (!cancelled) setError(cause) })
    return () => { cancelled = true }
  }, [reload])

  async function save(key: AdminAppSetting['key'], value: boolean | string[]) {
    setBusy(true)
    setSaved(false)
    try {
      const row = await updateAppSetting(key, value)
      setItems((current) => current?.map((item) => item.key === key ? row : item) ?? [row])
      setError(null)
      setSaved(true)
    } catch (cause) { setError(cause) }
    finally { setBusy(false) }
  }
  function submit(event: FormEvent) {
    event.preventDefault()
    const users = ids.split(/[\s,]+/).filter(Boolean)
    if (users.some((id) => id.includes('@'))) {
      setInputError('Enter internal user ids only. Copy each User id from its admin user page.')
      return
    }
    setInputError(null)
    void save('experiments.enabled_users', [...new Set(users)])
  }
  const listed = ids.split(/[\s,]+/).filter((id) => id !== '' && !id.includes('@'))

  return <div className="admin-page stack stack--wide">
    <header className="page"><h1>Settings</h1><p className="page__lead">Changes take effect on the next request and are recorded in the audit log.</p></header>
    {error != null && <ProblemNotice error={error} onRetry={() => setReload((value) => !value)}>The settings could not be read or saved.</ProblemNotice>}
    {saved && <Notice tone="info">Setting saved.</Notice>}
    <p role="status" className="settings-status">{items === null ? 'Loading settings…' : ''}</p>
    {items !== null && <>
      {items.filter((row) => typeof row.value === 'boolean').map((row) => <Card key={row.key} eyebrow={row.key}>
        <p>{row.value === true ? 'Enabled' : 'Disabled'}</p>
        <Button variant="secondary" disabled={busy} onClick={() => void save(row.key, row.value !== true)}>{row.value === true ? 'Disable' : 'Enable'} {row.key}</Button>
        {row.key === 'experiments.enabled' && <p>Experiments also require the deployment switch and membership in the user id list below. An empty list enables nobody.</p>}
      </Card>)}
      <Card eyebrow="Experiment access">
        <form id="experiments.enabled_users" className="stack" onSubmit={submit}>
          <TextArea label="Allowed internal user ids" hint={<>One id per line. Copy the User id from <Link className="auth__link" to="/admin/users">Users</Link>. An empty list enables nobody.</>}
            rows={6} value={ids} disabled={busy} error={inputError}
            onChange={(event) => { setIds(event.target.value); setSaved(false); setInputError(null) }} />
          {listed.length !== 0 && <ul>{[...new Set(listed)].map((id) => <li key={id}><Link className="auth__link" to={`/admin/users/${encodeURIComponent(id)}`}>{id}</Link></li>)}</ul>}
          <Button type="submit" busy={busy}>Save allowed user ids</Button>
        </form>
      </Card>
      <Link className="auth__link" to="/admin/audit">View audit log</Link>
    </>}
  </div>
}
